// Sound Manager for Fun Audio Effects
// This creates simple audio using Web Audio API and frequency synthesis

class SoundManager {
  constructor() {
    this.audioContext = null;
    this.sounds = {};
    this.isEnabled = true;
    this.volume = 0.3;
    
    // Initialize audio context on user interaction
    this.initAudioContext();
  }

  initAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
      this.isEnabled = false;
    }
  }

  // Resume audio context if suspended (required by browser policies)
  async resumeContext() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (error) {
        console.warn('Could not resume audio context:', error);
      }
    }
  }

  // Create a simple tone
  createTone(frequency, duration, type = 'sine') {
    if (!this.audioContext || !this.isEnabled) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
    oscillator.type = type;
    
    // Envelope for smooth attack and release
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(this.volume, this.audioContext.currentTime + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + duration);
    
    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + duration);
    
    return oscillator;
  }

  // Success sound - rising chord
  playSuccess() {
    this.resumeContext().then(() => {
      const notes = [261.63, 329.63, 392.00, 523.25]; // C, E, G, C
      notes.forEach((freq, index) => {
        setTimeout(() => {
          this.createTone(freq, 0.3, 'sine');
        }, index * 100);
      });
    });
  }

  // Error sound - descending tones
  playError() {
    this.resumeContext().then(() => {
      const notes = [400, 300, 200]; // Descending frequencies
      notes.forEach((freq, index) => {
        setTimeout(() => {
          this.createTone(freq, 0.2, 'square');
        }, index * 150);
      });
    });
  }

  // Start sound - upward sweep
  playStart() {
    this.resumeContext().then(() => {
      const startFreq = 200;
      const endFreq = 600;
      const duration = 0.5;
      
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.frequency.setValueAtTime(startFreq, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(endFreq, this.audioContext.currentTime + duration);
      oscillator.type = 'sawtooth';
      
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(this.volume, this.audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + duration);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);
    });
  }

  // Badge unlock sound - magical chime
  playBadge() {
    this.resumeContext().then(() => {
      const magicalNotes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      magicalNotes.forEach((freq, index) => {
        setTimeout(() => {
          this.createTone(freq, 0.8, 'sine');
          // Add harmonic for magical effect
          setTimeout(() => {
            this.createTone(freq * 2, 0.4, 'triangle');
          }, 50);
        }, index * 200);
      });
    });
  }

  // Click sound - short beep
  playClick() {
    this.resumeContext().then(() => {
      this.createTone(800, 0.1, 'square');
    });
  }

  // Hover sound - subtle tone
  playHover() {
    this.resumeContext().then(() => {
      this.createTone(1000, 0.05, 'sine');
    });
  }

  // Quiz complete sound - victory fanfare
  playComplete() {
    this.resumeContext().then(() => {
      const fanfare = [
        { freq: 523.25, time: 0 },     // C
        { freq: 659.25, time: 200 },   // E
        { freq: 783.99, time: 400 },   // G
        { freq: 1046.50, time: 600 },  // C
        { freq: 783.99, time: 800 },   // G
        { freq: 1046.50, time: 1000 }  // C
      ];
      
      fanfare.forEach(({ freq, time }) => {
        setTimeout(() => {
          this.createTone(freq, 0.3, 'triangle');
        }, time);
      });
    });
  }

  // Tick sound for timer
  playTick() {
    this.resumeContext().then(() => {
      this.createTone(1200, 0.05, 'square');
    });
  }

  // Warning sound for low time
  playWarning() {
    this.resumeContext().then(() => {
      for (let i = 0; i < 3; i++) {
        setTimeout(() => {
          this.createTone(800, 0.1, 'square');
        }, i * 200);
      }
    });
  }

  // Set volume (0-1)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }

  // Play sound by name
  play(soundName) {
    switch (soundName) {
      case 'correct':
      case 'success':
        this.playSuccess();
        break;
      case 'incorrect':
      case 'error':
        this.playError();
        break;
      case 'start':
        this.playStart();
        break;
      case 'badge':
        this.playBadge();
        break;
      case 'click':
        this.playClick();
        break;
      case 'hover':
        this.playHover();
        break;
      case 'complete':
        this.playComplete();
        break;
      case 'tick':
        this.playTick();
        break;
      case 'warning':
        this.playWarning();
        break;
      default:
        console.warn(`Sound '${soundName}' not found`);
    }
  }
}

// Create singleton instance
const soundManager = new SoundManager();

// Export convenient functions
export const playSound = (soundName) => {
  soundManager.play(soundName);
};

export const setSoundVolume = (volume) => {
  soundManager.setVolume(volume);
};

export const setSoundEnabled = (enabled) => {
  soundManager.setEnabled(enabled);
};

export default soundManager;